import { useCallback, useEffect, useState } from "react";
import { MessageSquare, Sparkles, Pencil, Trash2, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  GENERAL_ID,
  deleteChatEntry,
  listChatEntries,
  renameChatEntry,
  type ChatIndexEntry,
} from "@/lib/prometheusChatIndex";

export type ChatsSidebarListProps = {
  activeProjectId: string | null;
  onOpenGeneral: () => void;
  onOpenProjectChat: (projectId: string) => void;
};

function formatWhen(iso: string) {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t) || t < 1e10) return "";
  const diff = Date.now() - t;
  if (diff < 60_000) return "ahora";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}h`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Personal chat list: general + project chats, rename/delete, sorted by recent.
 */
export function ChatsSidebarList({
  activeProjectId,
  onOpenGeneral,
  onOpenProjectChat,
}: ChatsSidebarListProps) {
  const [entries, setEntries] = useState<ChatIndexEntry[]>(() => listChatEntries());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const refresh = useCallback(() => setEntries(listChatEntries()), []);

  useEffect(() => {
    refresh();
    const onStorage = () => refresh();
    window.addEventListener("storage", onStorage);
    window.addEventListener("prometheus-chat-index-changed", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("prometheus-chat-index-changed", onStorage);
    };
  }, [refresh, activeProjectId]);

  const startRename = (e: ChatIndexEntry, ev: { stopPropagation: () => void }) => {
    ev.stopPropagation();
    setEditingId(e.id);
    setDraft(e.title);
  };

  const commitRename = (id: string) => {
    renameChatEntry(id, draft);
    setEditingId(null);
    refresh();
    window.dispatchEvent(new Event("prometheus-chat-index-changed"));
  };

  const remove = (e: ChatIndexEntry, ev: { stopPropagation: () => void }) => {
    ev.stopPropagation();
    if (e.id === GENERAL_ID) return;
    if (!confirm(`¿Eliminar chat «${e.title}»?`)) return;
    deleteChatEntry(e.id);
    refresh();
    window.dispatchEvent(new Event("prometheus-chat-index-changed"));
    if (e.projectId && e.projectId === activeProjectId) onOpenGeneral();
  };

  return (
    <div className="flex flex-col gap-0.5 px-0.5 pr-6">
      <p className="px-2 pb-1 text-[8px] uppercase tracking-[0.16em] text-muted-foreground/60 font-bold">
        Conversaciones
      </p>

      {entries.map((entry) => {
        const isGeneral = entry.id === GENERAL_ID;
        const active = isGeneral
          ? !activeProjectId
          : Boolean(entry.projectId && entry.projectId === activeProjectId);
        const when = formatWhen(entry.updatedAt);

        return (
          <div
            key={entry.id}
            className={cn(
              "group relative rounded-md border text-left transition-colors",
              active
                ? "bg-muted/60 text-foreground border-border/80"
                : "text-muted-foreground border-transparent hover:text-foreground hover:bg-muted/30"
            )}
          >
            {editingId === entry.id ? (
              <div className="flex items-center gap-1 px-2 py-1.5">
                <input
                  value={draft}
                  onChange={(ev) => setDraft(ev.target.value)}
                  onKeyDown={(ev) => {
                    if (ev.key === "Enter") commitRename(entry.id);
                    if (ev.key === "Escape") setEditingId(null);
                  }}
                  className="flex-1 min-w-0 bg-transparent text-[11px] border border-border rounded px-1.5 py-0.5 text-foreground focus:outline-none focus:border-primary/50"
                  autoFocus
                />
                <button
                  type="button"
                  className="p-1 text-primary cursor-pointer"
                  onClick={() => commitRename(entry.id)}
                  aria-label="Guardar nombre"
                >
                  <Check className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  className="p-1 text-muted-foreground cursor-pointer"
                  onClick={() => setEditingId(null)}
                  aria-label="Cancelar"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  if (isGeneral) onOpenGeneral();
                  else if (entry.projectId) onOpenProjectChat(entry.projectId);
                }}
                className="flex items-start gap-2 w-full px-2 py-1.5 text-[11px] cursor-pointer"
              >
                {isGeneral ? (
                  <Sparkles className="w-3 h-3 mt-0.5 shrink-0 text-primary" />
                ) : (
                  <MessageSquare className="w-3 h-3 mt-0.5 shrink-0" />
                )}
                <span className="flex-1 min-w-0 leading-snug text-left">
                  <span className="font-bold block truncate">{entry.title}</span>
                  <span className="text-[9px] text-muted-foreground flex gap-1.5">
                    <span>{isGeneral ? "Sin proyecto" : "Proyecto"}</span>
                    {when ? <span>· {when}</span> : null}
                  </span>
                </span>
              </button>
            )}

            {editingId !== entry.id ? (
              <div className="absolute right-1 top-1 hidden group-hover:flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={(ev) => startRename(entry, ev)}
                  className="p-1 rounded hover:bg-muted cursor-pointer"
                  title="Renombrar"
                >
                  <Pencil className="w-2.5 h-2.5" />
                </button>
                {!isGeneral ? (
                  <button
                    type="button"
                    onClick={(ev) => remove(entry, ev)}
                    className="p-1 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive cursor-pointer"
                    title="Eliminar"
                  >
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
